import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageProvider, useLanguage } from "../i18n/LanguageContext";
import type { Language } from "../i18n/translations";

const LANGUAGES: { code: Language; label: string }[] = [
  { code: "gsw", label: "GSW" },
  { code: "de", label: "DE" },
  { code: "fr", label: "FR" },
  { code: "it", label: "IT" },
  { code: "en", label: "EN" },
];

export default function Home() {
  return (
    <LanguageProvider>
      <HomeContent />
    </LanguageProvider>
  );
}

function LanguageSwitcher() {
  const { lang, setLang } = useLanguage();
  return (
    <div className="flex gap-1">
      {LANGUAGES.map(({ code, label }) => (
        <button
          key={code}
          type="button"
          onClick={() => setLang(code)}
          className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
            lang === code
              ? "bg-wobisch-blue text-white"
              : "text-gray-500 hover:text-white"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function HomeContent() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [urlInput, setUrlInput] = useState("");
  const [slug, setSlug] = useState("");
  const [key, setKey] = useState("");
  const [error, setError] = useState("");
  const [manual, setManual] = useState(false);

  function parseWobischUrl(url: string): { slug: string; key: string } | null {
    try {
      const parsed = new URL(url);
      const pathSlug = parsed.pathname.replace(/^\//, "");
      const params = new URLSearchParams(parsed.hash.substring(1));
      const keyParam = params.get("key");
      if (pathSlug && keyParam) {
        return { slug: pathSlug, key: keyParam };
      }
    } catch {
      // not a valid URL
    }
    return null;
  }

  function handleUrlSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    const parsed = parseWobischUrl(urlInput.trim());
    if (parsed) {
      navigate(`/${parsed.slug}#key=${parsed.key}`);
    } else {
      setError(t("errorParseUrl"));
    }
  }

  function handleManualSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    const s = slug.trim();
    const k = key.trim();
    if (!s || !k) {
      setError(t("errorSlugKeyRequired"));
      return;
    }
    navigate(`/${s}#key=${k}`);
  }

  return (
    <div className="min-h-screen bg-topo text-gray-300">
      <div className="max-w-lg mx-auto px-4 py-16 flex flex-col items-center gap-12">
        {/* Header */}
        <header className="flex flex-col items-center gap-3">
          <div className="flex items-center gap-3">
            <img
              src="/logo-schrift.svg"
              alt="wobisch"
              className="h-8 w-auto invert"
            />
            <img src="/logo.svg" alt="" className="h-10 w-auto" />
          </div>
          <p className="text-gray-400">{t("tagline")}</p>
          <LanguageSwitcher />
        </header>

        {/* About */}
        <section className="text-center space-y-2">
          <p>{t("aboutText")}</p>
          <p className="text-gray-500 text-sm">{t("aboutBadges")}</p>
        </section>

        {/* Open Location */}
        <section className="w-full space-y-4">
          <h2 className="text-lg font-semibold text-white text-center">
            {t("openHeading")}
          </h2>

          {/* Mode toggle */}
          <div className="flex justify-center">
            <div className="inline-flex bg-[#1a1a1a] rounded-lg border border-gray-700 p-0.5">
              <button
                type="button"
                onClick={() => setManual(false)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  !manual
                    ? "bg-wobisch-blue text-white"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                {t("modeSimple")}
              </button>
              <button
                type="button"
                onClick={() => setManual(true)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  manual
                    ? "bg-wobisch-blue text-white"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                {t("modeManual")}
              </button>
            </div>
          </div>

          {!manual ? (
            <form onSubmit={handleUrlSubmit} className="space-y-2">
              <label className="block text-sm text-gray-400">
                {t("pasteLabel")}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder={t("urlPlaceholder")}
                  className="flex-1 bg-[#1a1a1a] border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-600 focus:outline-none focus:border-wobisch-blue"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-wobisch-blue text-white font-medium rounded-lg hover:bg-wobisch-blue/90 transition-colors"
                >
                  {t("openButton")}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleManualSubmit} className="space-y-2">
              <div className="space-y-2">
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder={t("slugPlaceholder")}
                  className="w-full bg-[#1a1a1a] border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-600 focus:outline-none focus:border-wobisch-blue"
                />
                <input
                  type="text"
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  placeholder={t("keyPlaceholder")}
                  className="w-full bg-[#1a1a1a] border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-600 focus:outline-none focus:border-wobisch-blue"
                />
              </div>
              <button
                type="submit"
                className="w-full py-2 bg-wobisch-blue text-white font-medium rounded-lg hover:bg-wobisch-blue/90 transition-colors"
              >
                {t("openButton")}
              </button>
            </form>
          )}

          {error && <p className="text-red-400 text-sm text-center">{error}</p>}
        </section>

        {/* Download */}
        <section className="w-full space-y-3">
          <h2 className="text-lg font-semibold text-white text-center">
            {t("getApp")}
          </h2>
          <div className="grid grid-cols-2 gap-2">
            <DownloadLink label="App Store" />
            <DownloadLink label="Google Play" />
            <DownloadLink label="F-Droid" />
            <DownloadLink label="Obtainium" />
          </div>
          <p className="text-center text-xs text-gray-500">
            {t("openSourceAfterBeta")}
          </p>
        </section>

        {/* FAQ */}
        <section className="w-full space-y-4">
          <h2 className="text-lg font-semibold text-white text-center">
            {t("faqHeading")}
          </h2>
          <div className="space-y-3">
            <FaqItem q={t("faqEncryptionQ")} a={t("faqEncryptionA")} />
            <FaqItem q={t("faqServerQ")} a={t("faqServerA")} />
            <FaqItem q={t("faqZeroKnowledgeQ")} a={t("faqZeroKnowledgeA")} />
            <FaqItem q={t("faqDurationQ")} a={t("faqDurationA")} />
            <FaqItem q={t("faqOpenSourceQ")} a={t("faqOpenSourceA")} />
          </div>
        </section>

        {/* Source */}
        <section className="text-center">
          <a
            href="https://github.com/hajime-ch/wobisch"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-white transition-colors text-sm"
          >
            {t("sourceLink")}
          </a>
        </section>

        {/* Footer */}
        <footer className="text-gray-600 text-xs text-center">
          {t("footer")}
        </footer>
      </div>
    </div>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#1a1a1a] transition-colors"
      >
        <span className="text-white text-sm font-medium">{q}</span>
        <span className="text-gray-500 text-xs ml-2">
          {open ? "\u2212" : "+"}
        </span>
      </button>
      {open && <div className="px-4 pb-3 text-sm text-gray-400">{a}</div>}
    </div>
  );
}

function DownloadLink({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-2 px-3 bg-[#1a1a1a] border border-gray-700 rounded-lg text-gray-500 text-sm font-medium cursor-default">
      {label}
      <span className="text-[10px] uppercase tracking-wide text-gray-600 bg-gray-800 px-1.5 py-0.5 rounded">
        soon
      </span>
    </div>
  );
}
